/**
 * The public event page's tabs: which exist, in what order, and which one a
 * pathname belongs to. Pure, so the nav, the layout and tests share one rule.
 * Order is fixed by design: Overview · Watch (only with feeds) · Draws · Players · Schedule · Matches ·
 * Venues · Merchandise. Players shows only to a viewer allowed to see entrants
 * and Venues only when the organiser listed venues; both are opt-in inputs.
 */
export type EventTabId =
  | 'overview'
  | 'watch'
  | 'draws'
  | 'players'
  | 'schedule'
  | 'matches'
  | 'venues'
  | 'merchandise';

export type EventTab = {
  id: EventTabId;
  path: string;
  live: boolean;
  /** Message key under `Event.navigation` when it differs from the id (Venues → "Venue" for exactly one). */
  labelKey?: string;
};

export function listEventTabs(opts: {
  hideCompetition: boolean;
  hasMerchandise: boolean;
  isLive: boolean;
  /**
   * The viewer may see the entrants (`getCanSeeRegistrants`). Absent means no
   * Players tab: the safe default. Not affected by `hideCompetition` — Players
   * is not a competition surface.
   */
  showPlayers?: boolean;
  /** Listed `event_venue` rows. 0 or absent means no Venues tab. */
  venueCount?: number;
  /** The event has court streams to show (`getCachedEventFeeds`). Absent means no Watch tab. */
  hasFeeds?: boolean;
  /** An app stream is live now: the Watch tab shows the live marker. */
  feedsLive?: boolean;
}): EventTab[] {
  const tabs: EventTab[] = [{ id: 'overview', path: '', live: false }];
  if (opts.hasFeeds) tabs.push({ id: 'watch', path: '/watch', live: opts.feedsLive === true });
  if (!opts.hideCompetition) tabs.push({ id: 'draws', path: '/divisions', live: false });
  if (opts.showPlayers) tabs.push({ id: 'players', path: '/players', live: false });
  if (!opts.hideCompetition) {
    tabs.push(
      { id: 'schedule', path: '/schedule', live: opts.isLive },
      { id: 'matches', path: '/matches', live: false },
    );
  }
  // Where an event is played is not competition data: Venues shows even when the
  // organiser hides Draws / Schedule / Matches.
  if ((opts.venueCount ?? 0) > 0) {
    tabs.push({
      id: 'venues',
      path: '/venues',
      live: false,
      labelKey: opts.venueCount === 1 ? 'venue' : 'venues',
    });
  }
  if (opts.hasMerchandise) tabs.push({ id: 'merchandise', path: '/merchandise', live: false });
  return tabs;
}

/** The path after `/event/{id}/{slug}`, or '' on the overview. */
function suffixOf(pathname: string): string {
  return pathname.match(/\/event\/[^/]+\/[^/]+(\/.*)?$/)?.[1] ?? '';
}

export function activeEventTab(pathname: string): EventTabId {
  const s = suffixOf(pathname);
  if (s.startsWith('/watch')) return 'watch';
  if (s.startsWith('/divisions') || s.startsWith('/draw')) return 'draws';
  if (s.startsWith('/players')) return 'players';
  if (s.startsWith('/schedule')) return 'schedule';
  if (s.startsWith('/matches')) return 'matches';
  if (s.startsWith('/venues')) return 'venues';
  if (s.startsWith('/merchandise')) return 'merchandise';
  return 'overview';
}

/** The court grid and a bracket need the full width; lists stay in the reading column. */
export function isWideEventPath(pathname: string): boolean {
  const s = suffixOf(pathname);
  return s.startsWith('/schedule') || s.startsWith('/draw/');
}
